import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import Paper from '@mui/material/Paper';
import { PERMISSIONS } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getVerifyPermission, constructQuery } from '@repo/shared/helpers';
import { Spinner } from '@repo/components';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { getPartnerCompanyDocuments } from 'src/api/partner';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/documents/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			filter: (search.filter as string) || undefined,
			page: (search.page as number | undefined) || 0,
		};
	},
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId } = Route.useParams();
	useBreadcrumbLevel(1, 'Documents');
	const searchParams = Route.useSearch();
	const queryClient = useQueryClient();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const navigate = useNavigate();

	const documentQuery = constructQuery({
		page: searchParams.page || 0,
		all: searchParams.filter === 'all' ? 'yes' : '',
	});

	const {
		data: documentsResponse,
		isLoading: documentsLoading,
		error: _getPartnerCompanyDocumentsError,
	} = useQuery({
		queryKey: ['partner-company-documents', partnerId, searchParams],
		queryFn: () => getPartnerCompanyDocuments(partnerId, documentQuery),
		enabled: !userLoading && !!authenticatedUser,
	});

	const {
		data: documentTypesResponse,
		isLoading: documentTypesLoading,
		error: _getPartnerCompanyDocumentTypesError,
	} = useQuery({
		queryKey: ['partner-company-document-types', partnerId],
		queryFn: () => getDocumentTypes(constructQuery({ noPagination: 'true', model: 'groups' })),
		enabled: !userLoading && !!authenticatedUser,
	});
	const documentTypes = documentTypesResponse?.data || [];

	if (!verifyPermission(PERMISSIONS.partnerCompanyEventsView)) {
		navigate({ to: `/partner/${partnerId}` });
		return null;
	}

	if (userLoading || documentsLoading || documentTypesLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	const handleRowClick = (id: string) => {
		navigate({ to: `/partner/${partnerId}/documents/${id}` });
		return null;
	};

	return (
		<div>
			<Paper variant="outlined" className="mt-2">
				<DocumentTab
					data={documentsResponse}
					documentTypes={documentTypes}
					revalidateDetail={() => queryClient.invalidateQueries({ queryKey: ['partner-company-documents', partnerId] })}
					apiUrl={`/api/partner-portal/groups/${partnerId}/files`}
					showFilterAndActionBox={true}
					onReload={() => queryClient.invalidateQueries({ queryKey: ['partner-company-documents', partnerId] })}
					onSelectDocument={handleRowClick}
					permissions={{
						metadata: verifyPermission([PERMISSIONS.partnerCompanyDocumentsView]),
						fullView: verifyPermission([PERMISSIONS.partnerCompanyDocumentsView]),
						upload: verifyPermission([PERMISSIONS.partnerCompanyDocumentsCreate]),
						archive: verifyPermission([PERMISSIONS.partnerCompanyDocumentsEdit]),
						edit: verifyPermission([PERMISSIONS.partnerCompanyDocumentsEdit]),
					}}
				/>
			</Paper>
		</div>
	);
}
